import type { PlainButtonProps } from '@customerio/pluma-components/react';
import { Button, Tooltip, TooltipContent } from '@customerio/pluma-components/react';
import type { IconName } from '@customerio/pluma-icons';
import { useMemo, type ComponentProps, type ReactNode, type Ref } from 'react';
import { mergeRefs } from 'react-merge-refs';

type IconButtonProps = {
  icon?: IconName;
  size?: 'sm' | 'md';
  label: string;
  tooltip?: React.ReactNode;
  isLoading?: boolean;
  children?: React.ReactNode;
  showLabel?: boolean;
  ref?: Ref<HTMLButtonElement>;
} & PlainButtonProps;

/**
 * Icon Button component for the top navigation actions.
 */
export function IconButton({ tooltip, ...props }: IconButtonProps) {
  return (
    <Tooltip isDisabled={tooltip == null} isCustom={true}>
      {(context) => (
        <>
          <IconButtonTrigger {...props} reference={context.reference} />
          <TooltipContent>{tooltip}</TooltipContent>
        </>
      )}
    </Tooltip>
  );
}

/** What a custom Tooltip hands its trigger: the reference setter and props. */
type TooltipReference = Parameters<
  Extract<ComponentProps<typeof Tooltip>['children'], (props: never) => ReactNode>
>[0]['reference'];

type IconButtonTriggerProps = Omit<IconButtonProps, 'tooltip'> & {
  reference: TooltipReference;
};

/**
 * The button itself, with its ref merged into the tooltip's once. A ref merged inline is a new
 * callback on every render, which React detaches and reattaches on every commit; the tooltip's
 * `setReference` sets state each time, so a re-render of the top navigation mid-navigation
 * looped until React gave up ("Maximum update depth exceeded"), taking the app shell down with it.
 */
function IconButtonTrigger({
  reference,
  icon,
  label,
  isLoading,
  children,
  showLabel = false,
  size = 'sm',
  ref,
  ...rest
}: IconButtonTriggerProps) {
  const { className, style, ...referenceProps } = rest;
  const { setReference, getReferenceProps } = reference;
  // `setReference` is stable, so this changes only with the caller's own ref.
  const mergedRef = useMemo(() => mergeRefs([ref, setReference]), [ref, setReference]);

  return (
    <Button
      ref={mergedRef}
      icon={icon}
      isIconOnly={icon != null && showLabel !== true}
      aria-label={showLabel ? undefined : label}
      variant="tertiary"
      size={size}
      isLoading={isLoading}
      {...referenceProps}
      className={className}
      style={style}
      {...getReferenceProps(referenceProps)}
    >
      {showLabel ? label : null}
      {children}
    </Button>
  );
}
